import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Grid,
    Paper,
    Typography,
    Button,
    Stack,
    Chip,
    TextField,
    Alert,
    CircularProgress,
    Avatar,
    IconButton,
    Snackbar,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Skeleton,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormGroup,
    FormControlLabel,
    Checkbox,
    TablePagination
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import StoreIcon from '@mui/icons-material/Store';
import PersonIcon from '@mui/icons-material/Person';
import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { useAdminVendorDetails } from 'hooks/useVendors';
import { useAdminAllMarketCategories } from 'hooks/useMarketCategory';
import { useAdminAllVendorStaff, useUpdateVendorStaffStatus, useAdminUpdateVendorStaff, useAdminDeleteVendorStaff } from 'hooks/useStaff';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import vendorService from 'api/vendorService';
import WalletFundManagement from 'views/admin/components/WalletFundManagement';
import BankAccountManager from 'views/common/bank-accounts/BankAccountManager';
import MapLocationPicker from 'views/common/MapLocationPicker';

export default function VendorDetailPage() {
    const { vendorId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // 🔹 State Management
    const [adminNote, setAdminNote] = useState('');
    const [error, setError] = useState('');
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [tabValue, setTabValue] = useState(0);

    // 🔹 Edit State
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({
        name: '',
        email: '',
        phoneNumber: '',
        address: '',
        marketCategoryId: '',
        lat: null,
        lng: null
    });

    // 🔹 Fetch Data
    const { data: vendorRes, isLoading, isError } = useAdminVendorDetails(vendorId);
    const vendor = vendorRes?.data;

    useEffect(() => {
        if (vendor) {
            setEditData({
                name: vendor.name || '',
                email: vendor.email || '',
                phoneNumber: vendor.phoneNumber || '',
                address: vendor.address || '',
                marketCategoryId: vendor.marketCategoryId || '',
                lat: vendor.location?.coordinates?.[1] || null,
                lng: vendor.location?.coordinates?.[0] || null
            });
        }
    }, [vendor]);

    const updateCustomerMutation = useMutation({
        mutationFn: (payload) => vendorService.adminUpdateVendor(vendor.id, payload),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['adminVendorDetails', vendorId]
            });
            setOpenSnackbar(true);
            setError('');
        },
        onError: (err) => setError(err?.response?.data?.message || 'Update failed')
    });

    const { data: marketCategoriesRes } = useAdminAllMarketCategories();
    const marketCategories = marketCategoriesRes?.data || [];

    const handleProfileSave = () => {
        updateCustomerMutation.mutate(editData, {
            onSuccess: () => setIsEditing(false)
        });
    };

    if (isLoading)
        return (
            <Box p={5} textAlign="center">
                <CircularProgress />
            </Box>
        );
    if (isError || !vendor) return <Alert severity="error">Vendor not found.</Alert>;

    const handleStatusUpdate = (newStatus) => {
        if ((newStatus === 'rejected' || newStatus === 'suspended') && !adminNote) {
            setError('Please provide a reason/note for this action.');
            return;
        }
        updateCustomerMutation.mutate({
            status: newStatus,
            adminNote,
            commissionRate: vendor.commissionRate
        });
    };

    return (
        <Box p={3} sx={{ background: '#f5f6fa', minHeight: '100vh' }}>
            <Stack direction="row" alignItems="center" spacing={2} mb={3}>
                <IconButton onClick={() => navigate('/admin/vendors')}>
                    <ArrowBackIcon />
                </IconButton>
                <Box>
                    <Typography variant="h4" fontWeight={700}>
                        {vendor.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Vendor ID: {vendor.id}
                    </Typography>
                </Box>
                <Box flexGrow={1} />
                <Stack direction="row" spacing={1}>
                    <Chip
                        label={`KYC: ${vendor.kycStatus?.replace('_', ' ').toUpperCase()}`}
                        color={
                            vendor.kycStatus === 'verified' || vendor.kycStatus === 'approved'
                                ? 'success'
                                : vendor.kycStatus === 'pending'
                                ? 'info'
                                : 'error'
                        }
                        variant="outlined"
                        sx={{ fontWeight: 700, px: 2 }}
                    />
                    <Chip
                        label={vendor.status?.toUpperCase()}
                        color={vendor.status === 'active' ? 'success' : 'warning'}
                        sx={{ fontWeight: 700, px: 2 }}
                    />
                </Stack>
            </Stack>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tabValue} onChange={(_, val) => setTabValue(val)}>
                    <Tab label="Overview" />
                    <Tab label="Staff Directory" />
                    <Tab label="Bank Accounts" />
                    <Tab label="Location" />
                    <Tab label="Owner Details" />
                </Tabs>
            </Box>

            {tabValue === 0 && (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={7}>
                        <Stack spacing={3}>
                            <Paper sx={{ p: 3, borderRadius: 3 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography variant="h6" fontWeight={600}>
                                        Store Profile
                                    </Typography>
                                    {!(vendor.kycStatus === 'verified' || vendor.kycStatus === 'approved') && (
                                        <Button
                                            startIcon={isEditing ? <CheckIcon /> : <EditIcon />}
                                            variant={isEditing ? 'contained' : 'outlined'}
                                            size="small"
                                            color={isEditing ? 'success' : 'primary'}
                                            onClick={isEditing ? handleProfileSave : () => setIsEditing(true)}
                                            disabled={updateCustomerMutation.isPending}
                                        >
                                            {isEditing ? 'Save Changes' : 'Edit Profile'}
                                        </Button>
                                    )}
                                </Stack>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <Avatar src={vendor.logo} variant="rounded" sx={{ width: '100%', height: 140, fontSize: 40 }}>
                                            <StoreIcon fontSize="inherit" />
                                        </Avatar>
                                    </Grid>
                                    <Grid item xs={12} sm={8}>
                                        <Stack spacing={1.5}>
                                            {isEditing ? (
                                                <>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        label="Store Name"
                                                        value={editData.name}
                                                        onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                                                    />
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        label="Email"
                                                        value={editData.email}
                                                        onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                                                    />
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        label="Phone"
                                                        value={editData.phoneNumber}
                                                        onChange={(e) => setEditData({ ...editData, phoneNumber: e.target.value })}
                                                    />
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        label="Address"
                                                        multiline
                                                        rows={2}
                                                        value={editData.address}
                                                        onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                                                    />
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Market Category</InputLabel>
                                                        <Select
                                                            value={editData.marketCategoryId}
                                                            label="Market Category"
                                                            onChange={(e) => setEditData({ ...editData, marketCategoryId: e.target.value })}
                                                        >
                                                            {marketCategories.map((cat) => (
                                                                <MenuItem key={cat.id} value={cat.id}>
                                                                    {cat.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </>
                                            ) : (
                                                <>
                                                    <DetailItem label="Owner Name" value={`${vendor.user?.firstName} ${vendor.user?.lastName}`} />
                                                    <DetailItem label="Email" value={vendor.email} />
                                                    <DetailItem label="Phone" value={vendor.phoneNumber} />
                                                    <DetailItem label="Address" value={vendor.address} />
                                                    <DetailItem
                                                        label="Category"
                                                        value={<Chip label={vendor.marketCategory?.name} size="small" />}
                                                    />
                                                </>
                                            )}
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Paper>

                            <Paper sx={{ p: 3, borderRadius: 3 }}>
                                <Typography variant="h6" fontWeight={600} mb={2}>
                                    KYC & Legal Documents
                                </Typography>
                                <Stack spacing={1}>
                                    {vendor.user?.kyc ? (
                                        <>
                                            {vendor.user.kyc.identity?.identityDocument && (
                                                <Button
                                                    variant="outlined"
                                                    fullWidth
                                                    sx={{ justifyContent: 'space-between', py: 1.5 }}
                                                    href={vendor.user.kyc.identity.identityDocument}
                                                    target="_blank"
                                                >
                                                    {`ID: ${vendor.user.kyc.identity.identityType || 'Identity Document'}`}
                                                    <Typography variant="caption">View Document</Typography>
                                                </Button>
                                            )}
                                            {vendor.user.kyc.address?.addressDocument && (
                                                <Button
                                                    variant="outlined"
                                                    fullWidth
                                                    sx={{ justifyContent: 'space-between', py: 1.5 }}
                                                    href={vendor.user.kyc.address.addressDocument}
                                                    target="_blank"
                                                >
                                                    Proof of Address
                                                    <Typography variant="caption">View Document</Typography>
                                                </Button>
                                            )}
                                            {vendor.user.kyc.guarantor?.identityDocument && (
                                                <Button
                                                    variant="outlined"
                                                    fullWidth
                                                    sx={{ justifyContent: 'space-between', py: 1.5 }}
                                                    href={vendor.user.kyc.guarantor.identityDocument}
                                                    target="_blank"
                                                >
                                                    Business / Reference Document
                                                    <Typography variant="caption">View Document</Typography>
                                                </Button>
                                            )}
                                            {vendor.user.kyc.passportUrl && (
                                                <Button
                                                    variant="outlined"
                                                    fullWidth
                                                    sx={{ justifyContent: 'space-between', py: 1.5 }}
                                                    href={vendor.user.kyc.passportUrl}
                                                    target="_blank"
                                                >
                                                    Passport Photograph
                                                    <Typography variant="caption">View Image</Typography>
                                                </Button>
                                            )}
                                            {!vendor.user.kyc.identity?.identityDocument &&
                                             !vendor.user.kyc.address?.addressDocument &&
                                             !vendor.user.kyc.guarantor?.identityDocument &&
                                             !vendor.user.kyc.passportUrl && (
                                                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                                                    No documents uploaded yet.
                                                </Typography>
                                            )}
                                        </>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                                            KYC Record not found for this user.
                                        </Typography>
                                    )}
                                </Stack>
                            </Paper>
                        </Stack>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <Stack spacing={3}>
                            <Paper sx={{ p: 3, borderRadius: 3 }}>
                                <Typography variant="h6" fontWeight={600} mb={2}>
                                    Review Action
                                </Typography>
                                {error && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {error}
                                    </Alert>
                                )}
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="Internal Notes"
                                    value={adminNote}
                                    onChange={(e) => setAdminNote(e.target.value)}
                                    sx={{ mb: 2 }}
                                />

                                <Stack spacing={1}>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        fullWidth
                                        startIcon={
                                            updateCustomerMutation.isPending ? (
                                                <CircularProgress size={20} color="inherit" />
                                            ) : (
                                                <VerifiedUserIcon />
                                            )
                                        }
                                        disabled={vendor.status === 'active' || updateCustomerMutation.isPending}
                                        onClick={() => handleStatusUpdate('active')}
                                    >
                                        Approve Store
                                    </Button>
                                    <Stack direction="row" spacing={1}>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            fullWidth
                                            disabled={vendor.status === 'rejected' || updateCustomerMutation.isPending}
                                            onClick={() => handleStatusUpdate('rejected')}
                                            startIcon={updateCustomerMutation.isPending && <CircularProgress size={16} color="inherit" />}
                                        >
                                            {updateCustomerMutation.isPending ? 'Processing...' : 'Reject'}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="warning"
                                            fullWidth
                                            disabled={vendor.status === 'suspended' || updateCustomerMutation.isPending}
                                            onClick={() => handleStatusUpdate('suspended')}
                                            startIcon={updateCustomerMutation.isPending && <CircularProgress size={16} color="inherit" />}
                                        >
                                            {updateCustomerMutation.isPending ? 'Processing...' : 'Suspend'}
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Paper>

                            <WalletFundManagement
                                userId={vendor.userId}
                                userRole={'vendor'}
                                currentWalletBalance={vendor.wallet?.availableBalance}
                            />
                        </Stack>
                    </Grid>
                </Grid>
            )}

            {tabValue === 1 && <VendorStaffSection vendorId={vendorId} />}

            {tabValue === 2 && (
                <Paper sx={{ p: 3, borderRadius: 3 }}>
                    <Typography variant="h6" fontWeight={600} mb={3}>
                        Vendor Bank Accounts
                    </Typography>
                    <BankAccountManager userId={vendor.userId} />
                </Paper>
            )}

            {tabValue === 3 && (
                <Paper sx={{ p: 3, borderRadius: 3 }}>
                    <Typography variant="h6" fontWeight={600} mb={3}>
                        Update Store Coordinates
                    </Typography>
                    <MapLocationPicker
                        initialLat={vendor.location?.coordinates?.[1]}
                        initialLng={vendor.location?.coordinates?.[0]}
                        onChange={(lat, lng) => setEditData((prev) => ({ ...prev, lat, lng }))}
                    />
                    <Box mt={3}>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={updateCustomerMutation.isPending}
                            onClick={() => updateCustomerMutation.mutate({
                                lat: parseFloat(editData.lat),
                                lng: parseFloat(editData.lng)
                            })}
                            startIcon={updateCustomerMutation.isPending && <CircularProgress size={16} color="inherit" />}
                        >
                            {updateCustomerMutation.isPending ? 'Updating...' : 'Update Store Coordinates'}
                        </Button>
                    </Box>
                </Paper>
            )}

            {tabValue === 4 && (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, borderRadius: 3 }}>
                            <Typography variant="h6" fontWeight={600} mb={3}>
                                Personal Information
                            </Typography>
                            <Stack spacing={2.5}>
                                <DetailItem label="Full Name" value={`${vendor.user?.firstName} ${vendor.user?.lastName}`} />
                                <DetailItem label="Email Address" value={vendor.user?.email} />
                                <DetailItem label="Phone Number" value={vendor.user?.phoneNumber} />
                                <DetailItem label="Gender" value={vendor.user?.gender} />
                                <DetailItem
                                    label="Account Status"
                                    value={
                                        <Chip
                                            label={vendor.user?.status?.toUpperCase()}
                                            size="small"
                                            color={vendor.user?.status === 'active' ? 'success' : 'error'}
                                        />
                                    }
                                />
                            </Stack>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, borderRadius: 3 }}>
                            <Typography variant="h6" fontWeight={600} mb={3}>
                                Verification Status
                            </Typography>
                            <Stack spacing={2.5}>
                                <DetailItem
                                    label="KYC Status"
                                    value={
                                        <Chip
                                            label={vendor.user?.kycStatus?.toUpperCase()}
                                            size="small"
                                            color={vendor.user?.kycStatus === 'verified' || vendor.user?.kycStatus === 'approved' ? 'success' : 'warning'}
                                        />
                                    }
                                />
                                <DetailItem
                                    label="NIN Status"
                                    value={
                                        <Chip
                                            label={vendor.user?.ninStatus?.toUpperCase()}
                                            size="small"
                                            color={vendor.user?.ninStatus === 'verified' ? 'success' : 'warning'}
                                        />
                                    }
                                />
                                <DetailItem label="Account Type" value={vendor.user?.accountType || 'Standard'} />
                                <DetailItem label="Joined Date" value={vendor.user?.createdAt ? new Date(vendor.user.createdAt).toLocaleDateString() : 'N/A'} />
                            </Stack>
                        </Paper>
                    </Grid>
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3, borderRadius: 3 }}>
                            <Typography variant="h6" fontWeight={600} mb={3}>
                                Personal Addresses
                            </Typography>
                            {vendor.user?.addresses?.length > 0 ? (
                                <Grid container spacing={2}>
                                    {vendor.user.addresses.map((addr, idx) => (
                                        <Grid item xs={12} sm={6} key={idx}>
                                            <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                                                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                                    {addr.label || `Address #${idx + 1}`} {addr.isDefault && <Chip label="Default" size="small" color="primary" sx={{ height: 16, fontSize: '0.6rem', ml: 1 }} />}
                                                </Typography>
                                                <Typography variant="body2">{addr.address}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {addr.city}, {addr.state} {addr.postcode}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : (
                                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                                    No personal addresses found for this user.
                                </Typography>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            )}

            <Snackbar
                open={openSnackbar}
                autoHideDuration={4000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity="success" variant="filled" sx={{ width: '100%' }}>
                    Vendor profile updated successfully!
                </Alert>
            </Snackbar>
            <ToastContainer position="top-right" autoHideDuration={5000} />
        </Box>
    );
}

const STAFF_PERMISSIONS = [
    { value: 'manage_inventory', label: 'Manage Inventory' },
    { value: 'manage_orders', label: 'Manage Orders' },
    { value: 'manage_staff', label: 'Manage Staff' },
    { value: 'manage_finance', label: 'Manage Finance' },
    { value: 'view_analytics', label: 'View Analytics' }
];

function VendorStaffSection({ vendorId }) {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [editingStaff, setEditingStaff] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const {
        data: staffData,
        isLoading,
        refetch
    } = useAdminAllVendorStaff({
        vendorId,
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined
    });

    const updateStatusMutation = useUpdateVendorStaffStatus();
    const updateMutation = useAdminUpdateVendorStaff();
    const deleteMutation = useAdminDeleteVendorStaff();

    const handleToggleStatus = (id, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
        if (window.confirm(`Are you sure you want to ${newStatus} this staff member?`)) {
            updateStatusMutation.mutate({ id, status: newStatus }, { onSuccess: () => refetch() });
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this staff member? This action cannot be undone.')) {
            try {
                await deleteMutation.mutateAsync(id);
                toast.success('Staff deleted successfully');
                refetch();
            } catch (err) {
                console.error(err);
            }
        }
    };

    const handleEdit = (staff) => {
        setEditingStaff(staff);
        setModalOpen(true);
    };

    const staffs = staffData?.data || [];
    const total = staffData?.count || 0;

    return (
        <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight={600}>
                    Vendor Staff List
                </Typography>
                <TextField
                    placeholder="Search staff..."
                    size="small"
                    sx={{ width: 300 }}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        )
                    }}
                />
            </Stack>

            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Staff Member</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Permissions</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading
                            ? [...Array(3)].map((_, i) => (
                                  <TableRow key={i}>
                                      <TableCell colSpan={5}>
                                          <Skeleton height={40} />
                                      </TableCell>
                                  </TableRow>
                              ))
                            : staffs.map((staff) => (
                                  <TableRow key={staff.id}>
                                      <TableCell>
                                          <Stack direction="row" spacing={1.5} alignItems="center">
                                              <Avatar sx={{ width: 32, height: 32 }}>
                                                  <PersonIcon fontSize="small" />
                                              </Avatar>
                                              <Box>
                                                  <Typography variant="body2" fontWeight={600}>
                                                      {staff.user?.firstName} {staff.user?.lastName}
                                                  </Typography>
                                                  <Typography variant="caption" color="text.secondary">
                                                      {staff.user?.email}
                                                  </Typography>
                                              </Box>
                                          </Stack>
                                      </TableCell>
                                      <TableCell>
                                          <Chip label={staff.role} size="small" variant="outlined" />
                                      </TableCell>
                                      <TableCell>
                                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                              {staff.permissions?.map((p) => (
                                                  <Chip
                                                      key={p}
                                                      label={p.replace('manage_', '')}
                                                      size="small"
                                                      variant="outlined"
                                                      sx={{ fontSize: '0.65rem' }}
                                                  />
                                              ))}
                                          </Stack>
                                      </TableCell>
                                      <TableCell>
                                          <Chip label={staff.status} size="small" color={staff.status === 'active' ? 'success' : 'error'} />
                                      </TableCell>
                                      <TableCell align="right">
                                          <IconButton color="primary" onClick={() => handleEdit(staff)}>
                                              <EditIcon fontSize="small" />
                                          </IconButton>
                                          <IconButton
                                              color={staff.status === 'active' ? 'error' : 'success'}
                                              onClick={() => handleToggleStatus(staff.id, staff.status)}
                                              disabled={updateStatusMutation.isPending}
                                          >
                                              {updateStatusMutation.isPending ? (
                                                  <CircularProgress size={20} />
                                              ) : (
                                                  <BlockIcon fontSize="small" />
                                              )}
                                          </IconButton>
                                          <IconButton color="error" onClick={() => handleDelete(staff.id)} disabled={deleteMutation.isPending}>
                                              <DeleteIcon fontSize="small" />
                                          </IconButton>
                                      </TableCell>
                                  </TableRow>
                              ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={total}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
            />

            <AdminEditStaffModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                staff={editingStaff}
                updateMutation={updateMutation}
                onSuccess={() => refetch()}
            />
        </Paper>
    );
}

VendorStaffSection.propTypes = {
    vendorId: PropTypes.string
};

function AdminEditStaffModal({ open, onClose, staff, updateMutation, onSuccess }) {
    const [formData, setFormData] = useState({
        role: 'staff',
        permissions: []
    });

    useEffect(() => {
        if (staff) {
            setFormData({
                role: staff.role || 'staff',
                permissions: staff.permissions || []
            });
        }
    }, [staff, open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handlePermissionChange = (perm) => {
        setFormData((prev) => {
            const has = prev.permissions.includes(perm);
            return {
                ...prev,
                permissions: has ? prev.permissions.filter((p) => p !== perm) : [...prev.permissions, perm]
            };
        });
    };

    const handleSubmit = async () => {
        try {
            await updateMutation.mutateAsync({ id: staff.id, payload: formData });
            toast.success('Staff permissions updated');
            onSuccess();
            onClose();
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Edit Staff Permissions</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={3} pt={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Updating: {staff?.user?.firstName} {staff?.user?.lastName}
                    </Typography>

                    <FormControl fullWidth>
                        <InputLabel>Role</InputLabel>
                        <Select name="role" value={formData.role} label="Role" onChange={handleChange}>
                            <MenuItem value="staff">Staff</MenuItem>
                            <MenuItem value="manager">Manager</MenuItem>
                        </Select>
                    </FormControl>

                    <Box>
                        <Typography variant="subtitle2" mb={1}>
                            Permissions
                        </Typography>
                        <FormGroup>
                            <Grid container spacing={1}>
                                {STAFF_PERMISSIONS.map((p) => (
                                    <Grid item xs={12} key={p.value}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={formData.permissions.includes(p.value)}
                                                    onChange={() => handlePermissionChange(p.value)}
                                                />
                                            }
                                            label={p.label}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </FormGroup>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={updateMutation.isPending}>
                    {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

AdminEditStaffModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    staff: PropTypes.object,
    updateMutation: PropTypes.object,
    onSuccess: PropTypes.func
};

function DetailItem({ label, value }) {
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.2 }}>
                {label}
            </Typography>
            <Typography variant="body2" fontWeight={500}>
                {value || 'Not provided'}
            </Typography>
        </Box>
    );
}

DetailItem.propTypes = {
    label: PropTypes.string,
    value: PropTypes.any
};
